import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Button, Paper } from "@mui/material";
import { useTour } from "../context/TourContext";

const STEPS = [
  {
    target: "tour-nav-gallery",
    page: "/gallery",
    title: "🖼️ Ma galerie",
    text: "C'est ta galerie personnelle : toutes tes photos et vidéos uploadées sont réunies ici. Clique sur un média pour l'afficher en plein écran. Passe la souris dessus pour le télécharger ou le supprimer.",
  },
  {
    target: "tour-add-media",
    page: "/gallery",
    title: "📤 Ajouter des médias",
    text: "Clique ici pour uploader des photos ou vidéos depuis ton appareil. Tu peux en envoyer plusieurs à la fois et ajouter un label à chacun avant l'envoi.\n\nUne fois ta galerie remplie, un bouton « Sélectionner » apparaît pour choisir plusieurs médias et les télécharger ensemble en ZIP.",
  },
  {
    target: "tour-nav-albums",
    page: "/albums",
    title: "📁 Mes albums",
    text: "Les albums permettent d'organiser tes médias par thème : vacances, anniversaire, famille… Tu peux ensuite partager chaque album avec des amis ou des groupes.",
  },
  {
    target: "tour-new-album",
    page: "/albums",
    title: "➕ Créer un album",
    text: "Clique ici pour créer un nouvel album. Une fois créé, ouvre-le pour y ajouter des médias depuis ta galerie. À l'intérieur de l'album, un bouton Partager te permet de le rendre visible à d'autres personnes ou à un groupe entier.",
  },
  {
    target: "tour-groups-title",
    page: "/albums",
    title: "👥 Les groupes de partage",
    text: "Les groupes te permettent de définir des listes de personnes (ex : « Famille », « Amis proches »). Au lieu de partager un album personne par personne, tu peux le partager directement avec tout un groupe d'un seul clic.",
  },
  {
    target: "tour-new-group",
    page: "/albums",
    title: "➕ Créer un groupe",
    text: "Clique ici pour créer un groupe. Tu pourras ensuite y inviter des membres par leur adresse email. S'ils ont déjà un compte, l'accès est immédiat ; sinon, ils recevront un lien pour s'inscrire. Tu peux retirer un membre à tout moment.",
  },
  {
    target: "tour-nav-shared",
    page: "/shared-with-me",
    title: "📬 Partagé avec moi",
    text: "Cette section liste tous les albums que d'autres personnes ont partagés avec toi. Tu peux les consulter et télécharger leur contenu, mais pas les modifier. C'est ici que tu verras les albums familiaux ou entre amis dont tu fais partie.",
  },
];

export { STEPS };

export default function GuidedTour() {
  const { active, step, next, prev, end } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [rect, setRect] = useState(null);
  const [, forceUpdate] = useState(0);

  const currentStep = STEPS[step];

  // Navigate to correct page when step changes
  useEffect(() => {
    if (!active || !currentStep) return;
    if (location.pathname !== currentStep.page) {
      navigate(currentStep.page);
    }
  }, [active, step]); // eslint-disable-line

  // Find and track target element position
  const updateRect = useCallback(() => {
    if (!active || !currentStep) return;
    const el = document.querySelector(`[data-tour="${currentStep.target}"]`);
    if (el) {
      setRect(el.getBoundingClientRect());
    } else {
      setRect(null);
    }
  }, [active, currentStep]);

  useEffect(() => {
    if (!active) return;
    const timers = [100, 300, 600, 1000].map((delay) =>
      setTimeout(() => {
        updateRect();
        forceUpdate((n) => n + 1);
      }, delay)
    );
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect);
    };
  }, [active, step, updateRect, location.pathname]);

  if (!active || !currentStep) return null;

  const PAD = 10;
  const MIN_SPOT = 30;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let spotTop = 0, spotLeft = 0, spotRight = vw, spotBottom = vh;
  if (rect && rect.width > 0) {
    const cx = (rect.left + rect.right) / 2;
    const cy = (rect.top + rect.bottom) / 2;
    const hw = Math.max(MIN_SPOT, rect.width / 2);
    const hh = Math.max(MIN_SPOT, rect.height / 2);
    spotTop = Math.max(0, cy - hh - PAD);
    spotLeft = Math.max(0, cx - hw - PAD);
    spotRight = Math.min(vw, cx + hw + PAD);
    spotBottom = Math.min(vh, cy + hh + PAD);
  }

  const tooltipWidth = 340;
  const showBelow = rect ? spotBottom < vh * 0.6 : false;
  const tooltipTopVal = rect
    ? showBelow
      ? spotBottom + 12
      : spotTop - 12 - 220
    : vh / 2 - 120;
  const tooltipLeftVal = rect
    ? Math.max(8, Math.min(vw - tooltipWidth - 8, spotLeft))
    : (vw - tooltipWidth) / 2;

  const overlay = {
    position: "fixed",
    backgroundColor: "rgba(0,0,0,0.72)",
    zIndex: 9000,
    pointerEvents: "none",
  };

  return (
    <>
      {/* Blocking click layer */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 8999 }}
        onClick={(e) => e.stopPropagation()}
      />
      {/* Top */}
      <div style={{ ...overlay, top: 0, left: 0, right: 0, height: spotTop }} />
      {/* Bottom */}
      <div style={{ ...overlay, top: spotBottom, left: 0, right: 0, bottom: 0 }} />
      {/* Left */}
      <div style={{ ...overlay, top: spotTop, left: 0, width: spotLeft, height: spotBottom - spotTop }} />
      {/* Right */}
      <div style={{ ...overlay, top: spotTop, left: spotRight, right: 0, height: spotBottom - spotTop }} />

      {/* Tooltip card */}
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          top: Math.max(8, tooltipTopVal),
          left: tooltipLeftVal,
          width: tooltipWidth,
          zIndex: 9001,
          p: 2.5,
          backgroundColor: "#1e293b",
          color: "#e2e8f0",
          border: "1px solid #334155",
          borderRadius: 2,
        }}
      >
        <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 0.5 }}>
          Étape {step + 1} / {STEPS.length}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: "#93c5fd" }}>
          {currentStep.title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.65, color: "#cbd5e1", whiteSpace: "pre-line" }}>
          {currentStep.text}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button size="small" onClick={end} sx={{ color: "#64748b", fontSize: "0.75rem" }}>
            Passer
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            {step > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={prev}
                sx={{ color: "#94a3b8", borderColor: "#334155", fontSize: "0.75rem" }}
              >
                ← Précédent
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={() => next(STEPS.length)}
              sx={{ backgroundColor: "#2563eb", fontSize: "0.75rem" }}
            >
              {step === STEPS.length - 1 ? "Terminer ✓" : "Suivant →"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
}
